import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import {
  useExpenseByIdQuery,
  useCreateDraftExpenseMutation,
  RecordType,
  CreateExpenseItemInput,
  FileInput,
  useExpenseFormDataQuery,
} from '../../../__generated__/graphql';
import { useToasts } from '../../../../../context/Toast';

import { ExpenseRecordTypeSelection } from './ExpenseRecordTypeSelection';
import { PER_DIEM_RECORD_TYPE_KEY, PerDiemForm } from './PerDiemForm';
import { ReimbursementForm } from './ReimbursementForm';
import { REIMBURSEMENT_RECORD_TYPE_KEY } from './reimbursementHelpers';
import { CarMileageForm, MILEAGE_RECORD_TYPE_KEY } from './CarMileageForm';
import {
  ExpenseItemRecordType,
  ExpenseRecordType,
  MeansOfTransport,
} from '../types';

type ExpenseSaveModalProps = {
  onClose: () => void;
  onSave: () => void;
  show: boolean;
  expenseGroupId?: string;
  refreshTrigger?: number;
  expenseId?: string | null;
};

export const ExpenseSaveModal: React.FC<ExpenseSaveModalProps> = ({
  show,
  onClose,
  onSave,
  expenseGroupId,
  refreshTrigger,
  expenseId,
}) => {
  const { t } = useTranslation();
  const { addSuccess, addError } = useToasts();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRecordType, setSelectedRecordType] =
    useState<RecordType | null>(null);
  const [createdExpenseId, setCreatedExpenseId] = useState<string | null>(null);

  const { data, loading, error } = useExpenseFormDataQuery({
    skip: !show,
  });
  const [createDraftExpense, { loading: creatingExpense }] =
    useCreateDraftExpenseMutation();

  const recordTypes = useMemo(() => {
    const expenseRecordTypes = data?.objectRecordTypes?.find(
      (ort) => ort.objectApiName === 'flair__Expense__c',
    );
    return expenseRecordTypes?.recordTypes || [];
  }, [data?.objectRecordTypes]);

  const extraRecordTypes = useMemo(() => {
    const expenseItemRecordTypes = data?.objectRecordTypes?.find(
      (ort) => ort.objectApiName === 'flair__Expense_Item__c',
    );
    return expenseItemRecordTypes?.recordTypes || [];
  }, [data?.objectRecordTypes]);

  const expenseIdToLoad = expenseId || createdExpenseId;
  const { data: expenseData, loading: expenseLoading } = useExpenseByIdQuery({
    variables: { id: expenseIdToLoad || '' },
    skip: !expenseIdToLoad || !show,
  });

  const isLoading = loading || expenseLoading || creatingExpense;
  const isEditing = !!expenseId;

  useEffect(() => {
    if (isEditing && expenseData?.expense?.recordType) {
      setCurrentStep(2);
      setSelectedRecordType(expenseData.expense.recordType as RecordType);
      setCreatedExpenseId(expenseId);
    } else if (!isEditing && !createdExpenseId && !selectedRecordType) {
      setCurrentStep(1);
      setSelectedRecordType(null);
      setCreatedExpenseId(null);
    }
  }, [isEditing, expenseData, expenseId, createdExpenseId, selectedRecordType]);

  const previousExpenseIdRef = React.useRef(expenseId);

  useEffect(() => {
    if (previousExpenseIdRef.current !== expenseId) {
      setSelectedRecordType(null);
      setCurrentStep(1);
      previousExpenseIdRef.current = expenseId;
    }
  }, [expenseId]);

  const handleRecordTypeSelect = async (recordType: RecordType) => {
    setSelectedRecordType(recordType);
    if (recordType.DeveloperName === ExpenseRecordType.PerDiem) {
      await handleCreatePerDiemExpense(recordType);
      setCurrentStep(2);
      return;
    }
    if (recordType.DeveloperName === ExpenseRecordType.Mileage) {
      await handleCreateMileageExpense(recordType);
      setCurrentStep(2);
      return;
    }
    if (recordType.DeveloperName === ExpenseRecordType.Reimbursement) {
      setCurrentStep(2);
      return;
    }
    setCurrentStep(2);
  };

  const handleCreatePerDiemExpense = async (recordType: RecordType) => {
    try {
      const result = await createDraftExpense({
        variables: {
          input: {
            expenseGroupId: expenseGroupId || null,
            recordTypeId: recordType.Id,
            dateFrom: new Date().toISOString().split('T')[0],
            expenseItems: [
              {
                recordTypeId: extraRecordTypes.find(
                  (rt) => rt.DeveloperName === 'Meal',
                )?.Id,
              } as CreateExpenseItemInput,
            ],
            alwaysCreateExpenseItem: true,
            extraFields: [],
            files: [],
          },
        },
      });
      if (result.data?.expenses?.createDraftExpense.recordId) {
        setCreatedExpenseId(result.data.expenses.createDraftExpense.recordId);
        addSuccess(t('expenses.perDiemForm.expenseCreatedSuccessfully'));
      } else {
        const errorMessage =
          result.data?.expenses?.createDraftExpense.error ||
          t('expenses.perDiemForm.expenseCreationFailed');
        addError(errorMessage);
        return;
      }
    } catch (err) {
      addError(t('expenses.perDiemForm.expenseCreationFailed'));
      return;
    }
  };

  const handleCreateMileageExpense = async (recordType: RecordType) => {
    try {
      const result = await createDraftExpense({
        variables: {
          input: {
            expenseGroupId: expenseGroupId || null,
            recordTypeId: recordType.Id,
            dateFrom: new Date().toISOString().split('T')[0],
            expenseItems: [
              {
                recordTypeId: extraRecordTypes.find(
                  (rt) => rt.DeveloperName === ExpenseItemRecordType.Travel,
                )?.Id,
                flair__Means_Of_Transport__c: MeansOfTransport.OwnCar,
              } as CreateExpenseItemInput,
            ],
            alwaysCreateExpenseItem: true,
            extraFields: [],
            files: [],
          },
        },
      });
      if (result.data?.expenses?.createDraftExpense.recordId) {
        setCreatedExpenseId(result.data.expenses.createDraftExpense.recordId);
        addSuccess(t('expenses.carMileageForm.expenseCreatedSuccessfully'));
      } else {
        const errorMessage =
          result.data?.expenses?.createDraftExpense.error ||
          t('expenses.carMileageForm.expenseCreationFailed');
        addError(errorMessage);
        return;
      }
    } catch (err) {
      addError(t('expenses.carMileageForm.expenseCreationFailed'));
      return;
    }
  };

  const handleCreateReimbursementExpense = async (
    recordType: RecordType,
    invoiceData: FileInput,
  ) => {
    try {
      const result = await createDraftExpense({
        variables: {
          input: {
            expenseGroupId: expenseGroupId || null,
            recordTypeId: recordType.Id,
            dateFrom: new Date().toISOString().split('T')[0],
            expenseItems: [
              {
                recordTypeId: extraRecordTypes.find(
                  (rt) => rt.DeveloperName === 'Other',
                )?.Id,
                flair__Means_Of_Transport__c: null,
              } as CreateExpenseItemInput,
            ],
            alwaysCreateExpenseItem: null,
            extraFields: [],
            files: [invoiceData],
          },
        },
      });
      if (result.data?.expenses?.createDraftExpense.recordId) {
        setCreatedExpenseId(result.data.expenses.createDraftExpense.recordId);
      } else {
        const errorMessage =
          result.data?.expenses?.createDraftExpense.error ||
          t('expenses.reimbursementForm.expenseCreationFailed');
        addError(errorMessage);
        return;
      }
    } catch (err) {
      console.error('Error creating Reimbursement expense:', err);
      addError(t('expenses.reimbursementForm.expenseCreationFailed'));
      return;
    }
  };

  const handleClose = () => {
    if (isEditing) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
      setSelectedRecordType(null);
    }
    setCreatedExpenseId(null);
    onClose();
  };

  const renderStepContent = () => {
    if (currentStep === 1 && !isEditing) {
      if (loading) {
        return (
          <div className="text-center py-4">
            {t('expenses.createEditModal.loading')}...
          </div>
        );
      }
      if (error || !recordTypes || recordTypes.length === 0) {
        return (
          <div className="text-center py-4 text-danger">
            {t('general.appError')}
          </div>
        );
      }

      return (
        <ExpenseRecordTypeSelection
          recordTypes={recordTypes as readonly RecordType[]}
          onSelectRecordType={handleRecordTypeSelect}
          loading={isLoading}
        />
      );
    }

    if (
      (currentStep === 2 || currentStep === 3 || isEditing) &&
      selectedRecordType
    ) {
      if (expenseLoading) {
        return (
          <div className="text-center py-5">
            <div
              className="spinner-border spinner-border-lg text-primary"
              role="status">
              <span className="visually-hidden">
                {t('expenses.createEditModal.loading')}
              </span>
            </div>
            <p className="mt-3 text-muted">
              {t('expenses.createEditModal.loading')}
            </p>
          </div>
        );
      }

      if (isEditing && !expenseData?.expense) {
        return (
          <div className="text-center py-4">
            {t('expenses.createEditModal.loading')}
          </div>
        );
      }
    }

    if (
      (currentStep === 2 || currentStep === 3 || isEditing) &&
      selectedRecordType
    ) {
      const commonProps = {
        onSave,
        onCancel: handleClose,
        expenseGroupId,
        refreshTrigger,
        expenseId: createdExpenseId,
        expenseFormData: data,
      };

      switch (selectedRecordType.DeveloperName) {
        case ExpenseRecordType.PerDiem:
          return (
            <PerDiemForm
              {...commonProps}
              loading={isLoading}
              recordTypeKey={PER_DIEM_RECORD_TYPE_KEY}
            />
          );
        case ExpenseRecordType.Reimbursement:
          return (
            <ReimbursementForm
              {...commonProps}
              loading={isLoading}
              recordType={selectedRecordType}
              onInvoiceUpload={handleCreateReimbursementExpense}
              recordTypeKey={REIMBURSEMENT_RECORD_TYPE_KEY}
            />
          );
        case ExpenseRecordType.Mileage:
          return (
            <CarMileageForm
              {...commonProps}
              loading={isLoading}
              recordTypeKey={MILEAGE_RECORD_TYPE_KEY}
            />
          );
        default:
          return (
            <div className="text-center py-4">
              {t('expenses.createEditModal.unsupportedRecordType')}
            </div>
          );
      }
    }

    return null;
  };

  const getModalSize = () => {
    if (
      selectedRecordType?.DeveloperName === ExpenseRecordType.PerDiem &&
      currentStep === 2
    ) {
      return 'lg';
    }
    return 'xl';
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size={getModalSize()}
      centered
      className="expense-save-modal"
      contentClassName="border"
      backdropClassName="expense-save-modal-backdrop">
      <Modal.Header closeButton className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <div className="mt-2">
              <small className="text-muted">{expenseData?.expense?.Name}</small>
            </div>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="expense-form-step">
          <div className="expense-form-step-content">{renderStepContent()}</div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
